/**
 * Confirmación de pagos con Banesco (spec 19, pedido del usuario 2026-10-23). El cajero consulta en el banco un pago
 * móvil o una transferencia antes de cobrar, con la API «Confirmación de Transacciones» (manual v1.7). La consulta la hace
 * SIEMPRE el servidor: la pantalla solo manda los datos que el cliente le da (banco, teléfono, referencia, fecha, monto).
 *
 * Formas de búsqueda del manual (sección VI) que usa el cobro:
 *  - `referencia_completa`  → transferencia Banesco a Banesco: la referencia completa o sus últimos 6 dígitos (no lleva
 *    fecha). El banco guarda estas transferencias con 6 dígitos: si la completa no aparece, el servidor busca con los 6.
 *  - `referencia_telefono`  → pago móvil (de Banesco o de otro banco): últimos 6 dígitos + teléfono + fecha + banco.
 *  - `referencia_cuenta`    → transferencia desde otro banco: últimos 6 dígitos + la cuenta de la tienda + fecha + banco.
 *  - `telefono_monto`       → pago móvil sin referencia: teléfono + fecha + monto.
 * La búsqueda «por fecha» (todos los movimientos del día) no se ofrece al cajero: mostraría los pagos de otros clientes.
 * Siempre 6 dígitos, aunque el manual acepta 4: con 4 el banco devuelve esos 4 y no se sabría qué pago es al buscarlo de
 * otra forma (ambiente de pruebas de Banesco, 2026-10-23).
 */
import { z } from 'zod';
import { decimalPositivo } from './comun.ts';

/** Bancos de Venezuela con su código (los 4 primeros dígitos de la cuenta). */
export const BANCOS_VE = [
  { codigo: '0102', nombre: 'Banco de Venezuela' },
  { codigo: '0104', nombre: 'Venezolano de Crédito' },
  { codigo: '0105', nombre: 'Mercantil' },
  { codigo: '0108', nombre: 'Provincial (BBVA)' },
  { codigo: '0114', nombre: 'Bancaribe' },
  { codigo: '0115', nombre: 'Banco Exterior' },
  { codigo: '0128', nombre: 'Banco Caroní' },
  { codigo: '0134', nombre: 'Banesco' },
  { codigo: '0137', nombre: 'Sofitasa' },
  { codigo: '0138', nombre: 'Banco Plaza' },
  { codigo: '0146', nombre: 'Bangente' },
  { codigo: '0151', nombre: 'BFC Banco Fondo Común' },
  { codigo: '0156', nombre: '100% Banco' },
  { codigo: '0157', nombre: 'DelSur' },
  { codigo: '0163', nombre: 'Banco del Tesoro' },
  { codigo: '0166', nombre: 'Banco Agrícola de Venezuela' },
  { codigo: '0168', nombre: 'Bancrecer' },
  { codigo: '0169', nombre: 'R4 Banco Microfinanciero' },
  { codigo: '0171', nombre: 'Banco Activo' },
  { codigo: '0172', nombre: 'Bancamiga' },
  { codigo: '0173', nombre: 'Banco Internacional de Desarrollo' },
  { codigo: '0174', nombre: 'Banplus' },
  { codigo: '0175', nombre: 'Banco Digital de los Trabajadores' },
  { codigo: '0177', nombre: 'Banfanb' },
  { codigo: '0178', nombre: 'N58 Banco Digital' },
  { codigo: '0191', nombre: 'BNC Banco Nacional de Crédito' },
] as const;

export const CODIGO_BANESCO = '0134';

/** Nombre del banco por su código («134» y «0134» son el mismo). null si no se conoce. */
export function nombreBanco(codigo: string | null | undefined): string | null {
  if (!codigo) return null;
  const c = codigoBanco(codigo);
  return BANCOS_VE.find((b) => b.codigo === c)?.nombre ?? null;
}

/** Código de banco con sus 4 dígitos («134» → «0134»). Lo que no son 3 o 4 dígitos queda tal cual. */
export function codigoBanco(codigo: string): string {
  const d = codigo.trim();
  return /^\d{3}$/.test(d) ? `0${d}` : d;
}

/**
 * Teléfono celular venezolano en el formato del banco: 58 + 10 dígitos («0414-123.45.67», «+58 414 1234567» y
 * «4141234567» dan «584141234567»). null si no es un celular válido.
 */
export function telefonoBanco(t: string): string | null {
  const d = t.replace(/\D/g, '');
  const n = d.length === 12 && d.startsWith('58') ? d : d.length === 11 && d.startsWith('0') ? `58${d.slice(1)}` : d.length === 10 ? `58${d}` : null;
  return n && /^584\d{9}$/.test(n) ? n : null;
}

/** Solo los dígitos de la referencia. */
export const digitosReferencia = (r: string) => r.replace(/\D/g, '');

export const METODOS_VERIFICABLES = ['pago_movil', 'transferencia'] as const;
export type MetodoVerificable = (typeof METODOS_VERIFICABLES)[number];
export const esMetodoVerificable = (m: string): m is MetodoVerificable => (METODOS_VERIFICABLES as readonly string[]).includes(m);

export type ModalidadBanesco = 'referencia_completa' | 'referencia_telefono' | 'referencia_cuenta' | 'telefono_monto';

/** Lo que el cajero escribe en «Verificar transacción». */
export interface DatosVerificacion {
  metodo: MetodoVerificable;
  banco_origen: string;
  fecha: string;
  referencia?: string | undefined;
  telefono?: string | undefined;
  monto?: string | undefined;
}

/** La referencia de la búsqueda parcial: sus últimos 6 dígitos. null si no alcanza. */
export function referenciaParcial(r: string): string | null {
  const d = digitosReferencia(r);
  return d.length >= 6 ? d.slice(-6) : null;
}

/**
 * Qué búsqueda del manual corresponde a lo escrito, o qué falta (con el campo para mostrar el error). La misma regla en
 * la pantalla y en el servidor.
 */
export function modalidadBanesco(e: DatosVerificacion): { modalidad: ModalidadBanesco } | { campo: keyof DatosVerificacion; error: string } {
  const ref = e.referencia ? digitosReferencia(e.referencia) : '';
  if (e.metodo === 'transferencia') {
    if (codigoBanco(e.banco_origen) === CODIGO_BANESCO) {
      if (ref.length < 6) return { campo: 'referencia', error: 'De Banesco a Banesco, escribe la referencia completa o sus últimos 6 dígitos' };
      return { modalidad: 'referencia_completa' };
    }
    if (!referenciaParcial(ref)) return { campo: 'referencia', error: 'Escribe la referencia: los últimos 6 dígitos' };
    return { modalidad: 'referencia_cuenta' };
  }
  const telefono = e.telefono ? telefonoBanco(e.telefono) : null;
  if (!telefono) return { campo: 'telefono', error: 'Escribe el celular del cliente desde donde pagó (0414-1234567)' };
  if (ref) {
    if (!referenciaParcial(ref)) return { campo: 'referencia', error: 'Escribe los últimos 6 dígitos de la referencia' };
    return { modalidad: 'referencia_telefono' };
  }
  if (!e.monto) return { campo: 'monto', error: 'Sin referencia, el banco busca por teléfono, fecha y monto: escribe el monto' };
  return { modalidad: 'telefono_monto' };
}

export const verificarTransaccionInput = z
  .object({
    metodo: z.enum(METODOS_VERIFICABLES),
    banco_origen: z.string().trim().regex(/^\d{3,4}$/, 'Elige el banco del cliente').transform(codigoBanco),
    fecha: z.iso.date('Fecha inválida'),
    referencia: z.string().trim().max(30).optional().transform((v) => (v && digitosReferencia(v) ? digitosReferencia(v) : undefined)),
    telefono: z.string().trim().max(25).optional().transform((v) => (v ? v : undefined)),
    monto: decimalPositivo.optional(),
  })
  .superRefine((e, ctx) => {
    const r = modalidadBanesco(e);
    if ('error' in r) ctx.addIssue({ code: 'custom', path: [r.campo], message: r.error });
  });
export type VerificarTransaccionInput = z.infer<typeof verificarTransaccionInput>;

/** Un movimiento que el banco devolvió, con lo que el cobro necesita saber de él. */
export interface TransaccionBancoDTO {
  /** null = no se puede usar para cobrar (ver `motivo`). */
  verificacion_id: string | null;
  referencia: string;
  /** Bs. */
  monto: string;
  fecha: string | null;
  hora: string | null;
  tipo: string;
  banco_origen: string | null;
  banco_origen_nombre: string | null;
  telefono_pagador: string | null;
  id_pagador: string | null;
  cuenta_origen: string | null;
  concepto: string | null;
  elegible: boolean;
  /** Por qué no se puede usar: no es un abono, no es a la cuenta de la tienda, ya se usó… */
  motivo: string | null;
  /** Documento o recibo donde ya se usó (si se usó). */
  usada_en: string | null;
  /** Coincide con el monto que se buscaba (null si no se dio monto). */
  coincide_monto: boolean | null;
  /** Aviso: una referencia que termina igual ya se registró a mano por el mismo monto. */
  aviso: string | null;
}

export interface VerificacionBancoDTO {
  modalidad: ModalidadBanesco;
  consultado_en: string;
  resultados: TransaccionBancoDTO[];
}

export interface BanescoEstadoDTO {
  /** El servidor tiene las credenciales y la cuenta de Banesco. */
  configurado: boolean;
  /** Simulador de desarrollo (nunca en producción). */
  simulado: boolean;
}
